import React, { useState, useEffect } from 'react';
import { 
  fetchAllPrograms, 
  fetchSessionsByProgram,
  fetchSOsByClientAndProgram,
  operationsBase,
  TABLES
} from '@/lib/airtable';
import { 
  getCachedData, 
  setCachedData, 
  CACHE_KEYS, 
  clearAllMaterialsCache 
} from '@/lib/cache';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  Upload,
  Calendar,
  Clock,
  HardDrive,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Search,
  Filter,
  Zap,
  BarChart3
} from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * MaterialsHome - Main materials management dashboard for admins
 * 
 * Provides overview of materials by program and session, quick stats,
 * and navigation to specific program/session material management.
 * 
 * Features:
 * - Program-level overview with session counts
 * - Recent uploads and version activity
 * - Storage usage statistics
 * - Quick access to upload new materials
 */
const MaterialsHome = () => {
  const [programs, setPrograms] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [storageStats, setStorageStats] = useState({
    totalFiles: 0,
    totalSize: '0 MB',
    activeVersions: 0
  });
  
  // Progressive loading states
  const [loadingStates, setLoadingStates] = useState({
    programs: true,
    storageStats: true,
    recentActivity: true
  });
  
  const [errors, setErrors] = useState({
    programs: null,
    storageStats: null,
    recentActivity: null
  });

  useEffect(() => {
    // Load programs data with caching
    const loadPrograms = async () => {
      try {
        // Try to get cached data first
        const cachedPrograms = getCachedData(CACHE_KEYS.PROGRAMS_LIST);
        if (cachedPrograms) {
          console.log('Using cached programs data');
          setPrograms(cachedPrograms);
          setLoadingStates(prev => ({ ...prev, programs: false }));
          return;
        }

        // Fetch all active programs from Airtable
        const programs = await fetchAllPrograms();
        
        // Simplify programs data without expensive metadata loading
        const enrichedPrograms = programs.map(program => ({
          id: program.id,
          name: program.name,
          code: program.name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 3),
          hasNewVersions: false // We'll load this separately if needed later
        }));
        
        // Cache the programs data
        setCachedData(CACHE_KEYS.PROGRAMS_LIST, enrichedPrograms);
        
        setPrograms(enrichedPrograms);
        setLoadingStates(prev => ({ ...prev, programs: false }));
        
      } catch (err) {
        console.error('Failed to load programs:', err);
        setErrors(prev => ({
          ...prev,
          programs: 'Failed to load programs data. Please check your connection and try again.'
        }));
        setLoadingStates(prev => ({ ...prev, programs: false }));
      }
    };

    // Load storage statistics
    const loadStorageStats = async () => {
      try {
        let stats = {
          totalFiles: 0,
          totalSize: '0 MB',
          activeVersions: 0
        };
        
        // Count material files
        const materialFilesResponse = await operationsBase(TABLES.MATERIAL_FILES)
          .select({
            fields: ['file_size_bytes'],
            maxRecords: 1000
          })
          .all();
        
        const totalFiles = materialFilesResponse.length;
        const totalBytes = materialFilesResponse.reduce((sum, record) => {
          const size = record.get('file_size_bytes') || 0;
          return sum + (typeof size === 'number' ? size : 0);
        }, 0);
        
        // Convert bytes to readable format
        let totalSize = '0 MB';
        if (totalBytes > 0) {
          const mb = totalBytes / (1024 * 1024);
          if (mb >= 1024) {
            totalSize = `${(mb / 1024).toFixed(1)} GB`;
          } else {
            totalSize = `${mb.toFixed(1)} MB`;
          }
        }
        
        // Skip Material_Versions queries - table may not exist yet
        // TODO: Re-enable when Material_Versions table is properly configured
        
        stats = {
          totalFiles,
          totalSize,
          activeVersions: 0 // Default until materials tables are set up
        };
        
        setStorageStats(stats);
        setLoadingStates(prev => ({ ...prev, storageStats: false }));
        
      } catch (statsError) {
        console.warn('Could not fetch storage statistics, using default values:', statsError);
        // Set default values instead of error state since materials tables may not exist yet
        setStorageStats({
          totalFiles: 0,
          totalSize: '0 MB',
          activeVersions: 0
        });
        setLoadingStates(prev => ({ ...prev, storageStats: false }));
      }
    };

    // Load recent activity
    const loadRecentActivity = async () => {
      try {
        // Skip Material_Versions queries - table may not exist yet
        // TODO: Re-enable when Material_Versions table is properly configured
        const recentVersions = [];
        
        const activity = recentVersions.map(record => ({
          id: record.id,
          action: record.get('change_notes')?.includes('Bulk') ? 'Bulk Upload' : 'Version Update',
          program: record.get('program_name') || 'Unknown Program',
          session: record.get('session_name') || 'All Sessions',
          version: record.get('version_number') || 'v1.0.0',
          timestamp: record.get('created_at') || new Date().toISOString(),
          admin: record.get('created_by') || 'system'
        }));
        
        setRecentActivity(activity);
        setLoadingStates(prev => ({ ...prev, recentActivity: false }));
        
      } catch (activityError) {
        console.warn('Could not fetch recent activity, using empty list:', activityError);
        // Set empty activity instead of error state since materials tables may not exist yet
        setRecentActivity([]);
        setLoadingStates(prev => ({ ...prev, recentActivity: false }));
      }
    };

    // Start all API calls in parallel for fastest loading
    loadPrograms();
    loadStorageStats();
    loadRecentActivity();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 48) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Helper function to render error state for a section
  const renderSectionError = (error, onRetry, sectionName) => (
    <Card>
      <CardContent className="p-6 text-center">
        <AlertTriangle className="h-6 w-6 text-red-500 mx-auto mb-2" />
        <p className="text-red-600 font-medium text-sm">
          Failed to load {sectionName}
        </p>
        <p className="text-gray-500 text-xs mt-1">
          {error}
        </p>
        <Button 
          onClick={onRetry} 
          variant="outline" 
          size="sm"
          className="mt-2"
        >
          Try Again
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900">
            Materials Management
          </h2>
          <p className="text-gray-600 mt-2">
            Manage program materials, versions, and distribution across all sessions
          </p>
        </div>
        <div className="flex space-x-3">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex items-center space-x-2"
            onClick={() => {
              clearAllMaterialsCache();
              window.location.reload();
            }}
            title="Clear cache and refresh data"
          >
            <Clock className="h-4 w-4" />
            <span>Refresh Data</span>
          </Button>
          <Button variant="outline" size="sm" className="flex items-center space-x-2">
            <Search className="h-4 w-4" />
            <span>Search Materials</span>
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex items-center space-x-2 text-blue-600 border-blue-300 hover:bg-blue-50"
            asChild
          >
            <Link to="/admin/materials/analytics">
              <BarChart3 className="h-4 w-4" />
              <span>Analytics</span>
            </Link>
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex items-center space-x-2 text-purple-600 border-purple-300 hover:bg-purple-50"
            asChild
          >
            <Link to="/admin/materials/bulk-operations">
              <Zap className="h-4 w-4" />
              <span>Bulk Operations</span>
            </Link>
          </Button>
          <Button size="sm" className="flex items-center space-x-2">
            <Upload className="h-4 w-4" />
            <span>Upload Materials</span>
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Total Files Card */}
        {loadingStates.storageStats ? (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Skeleton className="h-5 w-5" />
                <Skeleton className="h-4 w-20" />
              </div>
              <Skeleton className="h-8 w-16 mt-3" />
              <Skeleton className="h-3 w-32 mt-2" />
            </CardContent>
          </Card>
        ) : errors.storageStats ? (
          renderSectionError(errors.storageStats, () => window.location.reload(), 'storage stats')
        ) : (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-500">Total Files</span>
              </div>
              <div className="text-2xl font-bold mt-3">{storageStats.totalFiles}</div>
              <p className="text-xs text-gray-500 mt-1">Across all programs</p>
            </CardContent>
          </Card>
        )}

        {/* Storage Used Card */}
        {loadingStates.storageStats ? (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Skeleton className="h-5 w-5" />
                <Skeleton className="h-4 w-20" />
              </div>
              <Skeleton className="h-8 w-16 mt-3" />
              <Skeleton className="h-3 w-32 mt-2" />
            </CardContent>
          </Card>
        ) : errors.storageStats ? (
          renderSectionError(errors.storageStats, () => window.location.reload(), 'storage stats')
        ) : (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <HardDrive className="h-5 w-5 text-green-600" />
                <span className="text-sm font-medium text-gray-500">Storage Used</span>
              </div>
              <div className="text-2xl font-bold mt-3">{storageStats.totalSize}</div>
              <p className="text-xs text-gray-500 mt-1">Cloudflare R2 storage</p>
            </CardContent>
          </Card>
        )}

        {/* Active Versions Card */}
        {loadingStates.storageStats ? (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Skeleton className="h-5 w-5" />
                <Skeleton className="h-4 w-20" />
              </div>
              <Skeleton className="h-8 w-16 mt-3" />
              <Skeleton className="h-3 w-32 mt-2" />
            </CardContent>
          </Card>
        ) : errors.storageStats ? (
          renderSectionError(errors.storageStats, () => window.location.reload(), 'storage stats')
        ) : (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-purple-600" />
                <span className="text-sm font-medium text-gray-500">Active Versions</span>
              </div>
              <div className="text-2xl font-bold mt-3">{storageStats.activeVersions}</div>
              <p className="text-xs text-gray-500 mt-1">Currently in use</p>
            </CardContent>
          </Card>
        )}

        {/* Analytics Link Card - Always shown */}
        <Card 
          className="hover:shadow-md transition-shadow cursor-pointer border-2 border-dashed border-blue-300 hover:border-blue-500"
          asChild
        >
          <Link to="/admin/materials/analytics">
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center space-x-2 mb-3">
                <BarChart3 className="h-8 w-8 text-blue-600" />
              </div>
              <div className="text-lg font-bold text-blue-600 mb-1">View Analytics</div>
              <p className="text-xs text-blue-700">
                Usage insights & trends
              </p>
            </CardContent>
          </Link>
        </Card>
      </div>

      {/* Programs Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Programs & Materials</span>
          </CardTitle>
          <CardDescription>
            Select a program to manage its session materials and versions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingStates.programs ? (
            // Programs skeleton loading
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center space-x-3">
                      <Skeleton className="h-5 w-48" />
                      <Skeleton className="h-5 w-12" />
                      <Skeleton className="h-5 w-20" />
                    </div>
                    <div className="flex items-center space-x-4">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-28" />
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Skeleton className="h-9 w-32" />
                    <Skeleton className="h-9 w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : errors.programs ? (
            // Programs error state
            <div className="text-center py-8">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Programs</h3>
              <p className="text-gray-500 text-sm max-w-md mx-auto mb-4">
                {errors.programs}
              </p>
              <Button 
                onClick={() => window.location.reload()} 
                variant="outline"
              >
                Try Again
              </Button>
            </div>
          ) : programs.length === 0 ? (
            // No programs found
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Programs Found</h3>
              <p className="text-gray-500 text-sm max-w-md mx-auto">
                No programs are currently marked as active. Contact your administrator to set up programs 
                or check if you have the correct permissions to view materials.
              </p>
            </div>
          ) : (
            // Programs list
            <div className="space-y-4">
              {programs.map((program) => (
                <div 
                  key={program.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-medium text-gray-900">{program.name}</h3>
                      <Badge variant="secondary" className="text-xs">
                        {program.code}
                      </Badge>
                      {program.hasNewVersions && (
                        <Badge variant="default" className="text-xs bg-blue-100 text-blue-800">
                          New Versions
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      variant="default"
                      size="sm"
                      asChild
                      className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700"
                    >
                      <Link to={`/admin/materials/programs/${program.id}/materials`}>
                        <FileText className="h-4 w-4" />
                        <span>Program Materials</span>
                      </Link>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      asChild
                      className="flex items-center space-x-2"
                    >
                      <Link to={`/admin/materials/programs/${program.id}`}>
                        <span>Sessions</span>
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="h-5 w-5" />
            <span>Recent Activity</span>
          </CardTitle>
          <CardDescription>
            Latest material uploads and version changes
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingStates.recentActivity ? (
            // Recent activity skeleton loading
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 mt-0.5">
                    <Skeleton className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center space-x-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-12" />
                    </div>
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ))}
            </div>
          ) : errors.recentActivity ? (
            // Recent activity error state
            <div className="text-center py-6">
              <AlertTriangle className="h-6 w-6 text-red-500 mx-auto mb-2" />
              <p className="text-red-600 font-medium text-sm">Failed to load recent activity</p>
              <p className="text-gray-500 text-xs mt-1">{errors.recentActivity}</p>
              <Button 
                onClick={() => window.location.reload()} 
                variant="outline" 
                size="sm"
                className="mt-2"
              >
                Try Again
              </Button>
            </div>
          ) : recentActivity.length === 0 ? (
            // No recent activity
            <div className="text-center py-6">
              <FileText className="h-6 w-6 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No recent activity</p>
            </div>
          ) : (
            // Recent activity list
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 mt-0.5">
                    {activity.action === 'Version Update' ? (
                      <Upload className="h-4 w-4 text-blue-600" />
                    ) : (
                      <FileText className="h-4 w-4 text-green-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-sm text-gray-900">
                        {activity.action}
                      </span>
                      {activity.version && (
                        <Badge variant="secondary" className="text-xs">
                          {activity.version}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {activity.program} → {activity.session}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatTimestamp(activity.timestamp)} by {activity.admin}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MaterialsHome;